import { useIntl } from 'react-intl';
import { useUserTokenDecoded } from '../../../../redux/slices/appInfo/hooks';
import { useWorkspaceArtists } from '../../../accountSelector/useWorkspaceArtists';
import { PlanStatusVariant, usePlanStatus } from '../../hooks/usePlanStatus';
import { accountMenuMessages as messages } from './accountMenuMessages';

export interface AccountIdentity {
  /**
   * True with 2 or more own artists: the card names the workspace (`AccountWorkspaceName`), and
   * `name` is only the fallback for the art.
   */
  isWorkspace: boolean;
  /** The artist (1 own artist), or the email. */
  name: string;
  /** The artist picture. `null` for a workspace or an artist with no picture. */
  avatarUrl: string | null;
  /** "Pro plan", or "Max plan · 3 artists" for a workspace with 2 or more artists. */
  planLine: string;
  planVariant: PlanStatusVariant;
  email: string;
}

/**
 * design: navigation-v2 1a, 1e. The account card shows the artist and the plan when the workspace
 * has 1 own artist, and the workspace and the artist count when it has 2 or more.
 */
export function useAccountIdentity(): AccountIdentity {
  const intl = useIntl();
  const email = useUserTokenDecoded()?.email ?? '';
  const planStatus = usePlanStatus();
  const { ownAccounts, active } = useWorkspaceArtists();

  if (ownAccounts.length >= 2) {
    return {
      isWorkspace: true,
      name: email,
      avatarUrl: null,
      planLine: intl.formatMessage(messages.planAndArtists, { plan: planStatus.label, count: ownAccounts.length }),
      planVariant: planStatus.variant,
      email,
    };
  }
  const artist = active !== undefined && active.isDemo !== true ? active : ownAccounts[0];
  return {
    isWorkspace: false,
    name: artist?.name ?? email,
    avatarUrl: artist?.avatarProcessedUrl ?? null,
    planLine: planStatus.label,
    planVariant: planStatus.variant,
    email,
  };
}
